import IconChevronDown from '@apollo/icons/default/IconChevronDown.svg';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconError from '@apollo/icons/default/IconError.svg';
import IconLinter from '@apollo/icons/default/IconLinter.svg';
import IconOverflow from '@apollo/icons/default/IconOverflow.svg';
import IconServices from '@apollo/icons/default/IconServices.svg';
import IconSingleService from '@apollo/icons/default/IconSingleService.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import IconSync from '@apollo/icons/large/IconSync.svg';
import {
  ButtonGroup,
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
} from '@apollo/orbit';
import copy from 'copy-to-clipboard';
import * as monacoEditor from 'monaco-editor';
import graphqlPlugin from 'prettier/plugins/graphql';
import { format as formatPrettier } from 'prettier/standalone';
import QueryString from 'query-string';
import React, { useCallback } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';
import {
  useApolloGraphqlActions,
  useApolloGraphqlDiagnostics,
} from 'register-apollo-graphql-language';
import { UNATTRIBUTED_DIAGNOSTICS_URI } from 'register-apollo-graphql-language/src/ApolloGraphqlDiagnosticsProvider';

import {
  downloadJSONSdl,
  downloadRawSdl,
} from 'src/app/graph/documentationPage/sdlPage/editorActions/EditorActions';
import { executeEdits } from 'src/app/graph/explorerPage/explorerDocs/getFieldToggle/executeEdits';
import { useEditorModel } from 'src/app/graph/explorerPage/hooks/useEditorModel';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { format as formatLib } from 'src/lib/format';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { ProposalSchemaInfo } from '../../hooks/useProposalEditorState/useProposalEditorState';
import { DrawerVisibilityState } from '../../shared';

export const ProposalEditorActions = ({
  selectedSchemaInfo,
  editor,
  graphRef,
  setKeyboardModalVisible,
  setCurrentVisibleDrawer,
  sdlIsLoading,
}: {
  selectedSchemaInfo: ProposalSchemaInfo;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  graphRef: GraphRef;
  setKeyboardModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentVisibleDrawer: React.Dispatch<
    React.SetStateAction<DrawerVisibilityState>
  >;
  sdlIsLoading: boolean;
}) => {
  const shouldShowLinter = useLDFlag('nebula-proposals-linter');

  const [hasDismissedCompositionPopover, setHasDismissedCompositionPopover] =
    useLocalStorage('hasDismissedEditorCompositionPopover');

  const {
    autoComposition,
    setAutoComposition,
    triggerComposition,
    isComposing,
    invalidSubgraphs,
  } = useApolloGraphqlActions();

  const { [UNATTRIBUTED_DIAGNOSTICS_URI]: unattributedDiagnostics = [] } =
    useApolloGraphqlDiagnostics();

  const hasUnattributedDiagnostics = isMinLengthArray(
    1,
    unattributedDiagnostics,
  );

  // Diagnostics will either be all errors or all hints. If there are no
  // diagnostics, we should show error-related text / icons.
  const unattributedDiagnosticsDisplayState =
    !hasUnattributedDiagnostics ||
    unattributedDiagnostics[0].severity === monacoEditor.MarkerSeverity.Error
      ? 'error'
      : 'hint';

  const location = useLocation();
  const model = useEditorModel(editor);
  const handleFormatSchema = useCallback(async () => {
    if (!model) return;
    const currentUserSelection = editor.getSelection();
    const rangeToFormat =
      !currentUserSelection || currentUserSelection.isEmpty()
        ? model.getFullModelRange()
        : currentUserSelection;
    const startOffset = model.getOffsetAt(rangeToFormat.getStartPosition());
    const leadingWhiteSpace =
      model
        .getValue()
        .slice(0, startOffset)
        .match(/(^|\n)[ \t]*$/)?.[0] ?? '';

    // Include leading whitespace in range so prettier can remove it.
    const formatRange = monacoEditor.Range.fromPositions(
      new monacoEditor.Position(
        rangeToFormat.startLineNumber,
        rangeToFormat.startColumn - leadingWhiteSpace.length,
      ),
      rangeToFormat.getEndPosition(),
    );
    try {
      const formattedText = (
        await formatPrettier(model.getValueInRange(formatRange), {
          parser: 'graphql',
          plugins: [graphqlPlugin],
        })
      ).trimEnd(); // prettier will add a newline at the end of the query, get rid of that.
      executeEdits(
        {
          type: 'format',
          variableEdits: [],
          edits: [{ text: formattedText, range: formatRange }],
        },
        editor,
      );
    } catch {
      // failed to format text, do nothing.
      // This is likely caused by the query being invalid
    }
  }, [editor, model]);

  return (
    <div className="flex items-center justify-between px-6 pt-3">
      <div className="flex items-center">
        {selectedSchemaInfo.type === 'Graph' ? (
          <IconServices className="mr-2 text-icon-primary" />
        ) : (
          <IconSingleService className="mr-2 text-icon-primary" />
        )}
        <span className="text-base font-semibold">
          {selectedSchemaInfo.displayName}
        </span>
      </div>

      <div className="flex items-center space-x-2">
        {selectedSchemaInfo.type === 'Subgraph' && (
          <>
            <Tooltip
              label={`${
                unattributedDiagnostics.length
              } unattributed ${formatLib.pluralize(
                unattributedDiagnostics.length,
                unattributedDiagnosticsDisplayState,
              )}`}
            >
              <IconButton
                aria-label={`${
                  unattributedDiagnostics.length
                } unattributed ${formatLib.pluralize(
                  unattributedDiagnostics.length,
                  'error',
                )}`}
                variant="hidden"
                size="sm"
                icon={
                  unattributedDiagnosticsDisplayState === 'error' ? (
                    <IconError
                      className={
                        // only override the color if there are errors, else it
                        // should appear gray/disabled
                        hasUnattributedDiagnostics ? '!text-icon-error' : ''
                      }
                    />
                  ) : (
                    <IconWarning className="!text-icon-warning" />
                  )
                }
                isDisabled={!hasUnattributedDiagnostics}
                onClick={() =>
                  setCurrentVisibleDrawer('unattributedDiagnostics')
                }
              />
            </Tooltip>
            <ButtonGroup isAttached>
              <Tooltip
                label={
                  !hasDismissedCompositionPopover
                    ? 'New! Try composing your graph to see composition warnings and errors.'
                    : invalidSubgraphs.length > 0
                      ? `Some subgraphs have errors, fix those to compose: ${invalidSubgraphs
                          .map((uri) => {
                            const subgraphName = QueryString.parse(
                              monacoEditor.Uri.parse(uri).query,
                            ).subgraph_name;

                            if (typeof subgraphName === 'string') {
                              return subgraphName;
                            } else {
                              return 'Unknown';
                            }
                          })
                          .join(', ')}`
                      : isComposing
                        ? 'Composing graph...'
                        : autoComposition
                          ? 'Compose on change enabled'
                          : 'Compose graph'
                }
                defaultIsOpen={!hasDismissedCompositionPopover}
                onClose={() => setHasDismissedCompositionPopover(true)}
              >
                <IconButton
                  variant="hidden"
                  isDisabled={sdlIsLoading || invalidSubgraphs.length > 0}
                  type="button"
                  size="sm"
                  isLoading={isComposing}
                  aria-label="Compose graph"
                  onClick={() => triggerComposition()}
                  icon={
                    <>
                      <IconServices />
                      {autoComposition && (
                        // TODO: orbit currently sets the size and color of icons inside buttons in a way that can't be overridden
                        // so we need to use `!important` for this style for now.
                        <IconSync className="absolute right-1 top-1 !size-2" />
                      )}
                    </>
                  }
                />
              </Tooltip>
              <Menu placement="bottom-end" autoSelect={false}>
                <Tooltip label="More composition options">
                  <MenuButton
                    as={IconButton}
                    aria-label="More composition options"
                    variant="hidden"
                    size="sm"
                    type="button"
                  >
                    <IconChevronDown />
                  </MenuButton>
                </Tooltip>
                <MenuContent>
                  <MenuList>
                    <MenuOptionGroup
                      type="checkbox"
                      value={autoComposition ? ['autoComposition'] : []}
                      onChange={(value) => {
                        setAutoComposition(value.includes('autoComposition'));
                      }}
                    >
                      <MenuItemOption value="autoComposition" type="checkbox">
                        Compose on change
                      </MenuItemOption>
                    </MenuOptionGroup>
                  </MenuList>
                </MenuContent>
              </Menu>
            </ButtonGroup>
          </>
        )}
        {shouldShowLinter && selectedSchemaInfo.type === 'Subgraph' && (
          <Tooltip label="Lint schema" placement="top">
            <IconButton
              type="button"
              isDisabled={sdlIsLoading}
              variant="hidden"
              size="sm"
              aria-label="Lint schema"
              icon={<IconLinter />}
              as={Link}
              to={proposalEditorRouteConfig.locationFrom({
                location,
                patch: {
                  linkedCoordinateName: RouteConfig.DELETE,
                  linkedCommentId: RouteConfig.DELETE,
                },
                fromRouteConfig: proposalEditorRouteConfig,
              })}
              onClick={() => setCurrentVisibleDrawer('linter')}
            />
          </Tooltip>
        )}
        <Tooltip
          label="Copy the entire SDL file"
          placement="top"
          clickedLabel="Copied"
        >
          <IconButton
            variant="hidden"
            type="button"
            size="sm"
            isDisabled={sdlIsLoading}
            aria-label="Copy the entire SDL file"
            icon={<IconCopy />}
            onClick={() => {
              copy(editor.getValue());
            }}
          />
        </Tooltip>
        <Menu>
          <MenuButton
            as={IconButton}
            isDisabled={sdlIsLoading}
            variant="hidden"
            size="sm"
            type="button"
          >
            <IconOverflow />
          </MenuButton>

          <MenuContent>
            <MenuList>
              <MenuItem
                onClick={() =>
                  downloadJSONSdl(
                    editor.getValue(),
                    graphRef,
                    selectedSchemaInfo.displayName,
                  )
                }
              >
                Download as JSON
              </MenuItem>
              <MenuItem
                onClick={() =>
                  downloadRawSdl(
                    editor.getValue(),
                    graphRef,
                    selectedSchemaInfo.displayName,
                  )
                }
              >
                Download Raw
              </MenuItem>
              {selectedSchemaInfo.type === 'Subgraph' && (
                <>
                  <MenuItem onClick={handleFormatSchema}>
                    Format Document
                  </MenuItem>
                  <MenuItem onClick={() => setKeyboardModalVisible(true)}>
                    View keyboard shortcuts
                  </MenuItem>
                </>
              )}
            </MenuList>
          </MenuContent>
        </Menu>
      </div>
    </div>
  );
};
